/**
 * Экран «Вместе» · окно 4 запуска — статистика участников.
 *
 * Живёт вкладкой рядом с «Наградами» (принятый вид прототипа Ф2.0: «Награды · Вместе»),
 * поэтому нижняя навигация осталась на пяти пунктах, как решили на дизайн-воротах.
 *
 * Данные — `GET /leaderboard` (`core/leaderboard.py`): дни занятий по часам КАЖДОГО,
 * ответы, «занимаются N из M». ⛔ Витрина не считает ни одной цифры и не сортирует
 * строки заново: порядок пришёл из ядра.
 *
 * ⭐ Про имя спрашиваем ровно один раз и только у того, кто уже попал в таблицу.
 *   Отказ записывается (`stats_opt_in = 0`) — второй раз не пристаём.
 *
 * Тексты и раскладка живут в `src/leaderboard.ts` и проверяются запуском:
 *   cd Lang/web && node tools/check-leaderboard.mjs
 */

import "../styles/leaderboard.css";

import { leaderboard as fetchLeaderboard } from "../api";
import {
  CONSENT_DETAIL, TABS, answersLabel, consentSummary, daysLabel, emptyLabel,
  membersHint, meRow, shouldAsk, sinceLabel, studyingLabel, whenLabel,
  type LbTable, type PeriodKey,
} from "../leaderboard";
import { render } from "../router";
import { setStatsOptIn, state } from "../state";
import { tap } from "../tg";

let period: PeriodKey = "week";
let loading = false;
let failed = false;

function esc(s: unknown): string {
  return String(s ?? "").replace(/[&<>"']/g, (ch) =>
    ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[ch] as string));
}

/** Сходить за свежей таблицей. Экран при этом показывает то, что уже есть. */
async function load(): Promise<void> {
  if (loading) return;
  loading = true;
  failed = false;
  try {
    state.leaderboard = await fetchLeaderboard();
  } catch {
    failed = true;                        // 403 ловит api.ts и ставит замок — тут молчим
  } finally {
    loading = false;
    render("awards");
  }
}

/** Открыли вкладку — перечитали. Цифры чужих людей меняются без нашего участия. */
export function refreshTogether(): void {
  void load();
}

function rowHtml(r: LbTable["rows"][number], today: string | null): string {
  return (
    `<div class="lbrow${r.is_me ? " me" : ""}">` +
      `<div class="who">${esc(r.name)}${r.is_me ? '<span class="you">это вы</span>' : ""}` +
        `${r.named ? "" : '<span class="quiet">имя скрыто</span>'}</div>` +
      `<div class="d">${esc(daysLabel(r.days))}</div>` +
      `<div class="a">${esc(answersLabel(r.answers))}</div>` +
      `<div class="w">${esc(whenLabel(r.last_day, today))}</div>` +
    "</div>"
  );
}

function tableHtml(t: LbTable | undefined): string {
  if (!t) return `<p class="empty">${esc(emptyLabel(period))}</p>`;
  const today = meRow(t)?.last_day ?? null;   // «сегодня» берём из дня, что прислало ядро
  const rows = t.rows.length
    ? t.rows.map((r) => rowHtml(r, today)).join("")
    : `<p class="empty">${esc(emptyLabel(t.period))}</p>`;
  return (
    `<p class="hsub">${esc(sinceLabel(t))}</p>` +
    '<div class="lbhead"><div class="who">Кто</div><div class="d">Дней</div>' +
      '<div class="a">Ответов</div><div class="w">Занимался</div></div>' +
    rows +
    `<p class="total">${esc(studyingLabel(t))}</p>` +
    `<p class="footnote">${esc(membersHint(t.total_members))}</p>`
  );
}

function askHtml(): string {
  return (
    '<div class="card ask">' +
      '<p class="h">Показывать твоё имя?</p>' +
      `<p class="hsub">${esc(CONSENT_DETAIL)}</p>` +
      '<div class="askrow">' +
        '<button type="button" class="askbtn yes" data-consent="1">Показывать имя</button>' +
        '<button type="button" class="askbtn no" data-consent="0">Не показывать</button>' +
      "</div>" +
      '<p class="footnote">Передумать можно в настройках в любой момент.</p>' +
    "</div>"
  );
}

export function renderTogether(host: HTMLElement): void {
  const data = state.leaderboard;
  if (!data) {
    host.innerHTML = failed
      ? '<div class="stub"><p>Не получилось собрать таблицу. Она появится, ' +
        "как только связь вернётся.</p></div>"
      : '<div class="stub"><p>Считаем, кто сколько занимался…</p></div>';
    if (!loading && !failed) void load();
    return;
  }

  const t = data.periods[period] ?? data.periods.week;
  const tabs = TABS.map((tab) =>
    `<button type="button" data-lbtab="${tab.key}" ` +
    `aria-pressed="${tab.key === period}">${tab.label}</button>`).join("");

  host.innerHTML =
    '<div class="two-col">' +
      '<div class="col-main">' +
        '<div class="greet"><h2>Вместе</h2>' +
        `<span class="streak">👥 ${t ? t.studying : 0}</span></div>` +
        (shouldAsk(data) ? askHtml() : "") +
        '<div class="card">' +
          `<div class="lbtabs" role="group" aria-label="Период">${tabs}</div>` +
          tableHtml(t) +
        "</div>" +
      "</div>" +
      '<div class="col-side">' +
        '<div class="card">' +
          '<p class="h">Как читать</p>' +
          '<p class="hsub">Главная цифра — <b>дни занятий</b>: она про привычку. ' +
          "Ответы стоят второй цифрой, чтобы много нажатий за один заход не выглядели " +
          "как усердие.</p>" +
          `<p class="footnote">${esc(consentSummary(data.opt_in))}</p>` +
        "</div>" +
      "</div>" +
    "</div>";

  host.querySelector(".lbtabs")?.addEventListener("click", (e) => {
    const btn = (e.target as HTMLElement).closest<HTMLElement>("button[data-lbtab]");
    if (!btn) return;
    tap(btn);
    period = btn.dataset.lbtab as PeriodKey;
    render("awards");
  });

  host.querySelector(".askrow")?.addEventListener("click", (e) => {
    const btn = (e.target as HTMLElement).closest<HTMLElement>("button[data-consent]");
    if (!btn) return;
    tap(btn);
    // ⚠ Перерисовать надо ДВАЖДЫ: сразу — чтобы карточка спроса ушла в тот же миг
    // (иначе человек жмёт, а на экране ничего не меняется — поймано живым стендом),
    // и после ответа сервера — там уже настоящие имена от ядра.
    void setStatsOptIn(btn.dataset.consent === "1").then(() => render("awards"));
    render("awards");
  });
}
